import { useMemo, useState } from "react";
import {
  FiCheck,
  FiFilter,
  FiLoader,
  FiPackage,
  FiPlus,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const emptyFilters = {
  query: "",
  brandId: "",
  categoryId: "",
  subCategoryId: "",
  seasonId: "",
  productId: "",
  storeId: "",
  colorId: "",
  sizeId: "",
  gstRuleGroup: "",
  lotSearch: "",
};

export default function SalesOrderProductFilterModal({
  open,
  onClose,
  filterOptions,
  selectedStoreId,
  onAddItems,
  accessToken,
}) {
  const [filters, setFilters] = useState({
    ...emptyFilters,
    storeId: selectedStoreId || "",
  });
  const [results, setResults] = useState([]);
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");

  const updateFilter = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
      ...(field === "categoryId" ? { subCategoryId: "" } : {}),
    }));
  };

  const subCategories = useMemo(
    () =>
      (filterOptions?.subCategories || []).filter(
        (subCategory) =>
          !filters.categoryId || subCategory.categoryId === filters.categoryId,
      ),
    [filterOptions, filters.categoryId],
  );

  const products = useMemo(
    () =>
      (filterOptions?.products || []).filter((product) => {
        if (filters.brandId && product.brandId !== filters.brandId) {
          return false;
        }

        if (filters.categoryId && product.categoryId !== filters.categoryId) {
          return false;
        }

        if (
          filters.subCategoryId &&
          product.subCategoryId !== filters.subCategoryId
        ) {
          return false;
        }

        if (filters.seasonId && product.seasonId !== filters.seasonId) {
          return false;
        }

        return true;
      }),
    [filterOptions, filters],
  );

  const searchProducts = async () => {
    try {
      setIsSearching(true);
      setError("");

      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        }
      });

      const catalog = await apiRequest(
        `/sales-orders/catalog?${params.toString()}`,
        { accessToken },
      );

      setResults(catalog);
      setSelectedVariantIds(catalog.map((variant) => variant.id));
    } catch (requestError) {
      setError(requestError.message || "Unable to load products.");
    } finally {
      setIsSearching(false);
    }
  };

  const toggleVariant = (variantId) => {
    setSelectedVariantIds((previous) =>
      previous.includes(variantId)
        ? previous.filter((id) => id !== variantId)
        : [...previous, variantId],
    );
  };

  const addSelectedProducts = () => {
    const selectedProducts = results.filter((variant) =>
      selectedVariantIds.includes(variant.id),
    );

    if (!selectedProducts.length) {
      setError("Select at least one variant to add.");
      return;
    }

    onAddItems(selectedProducts, filters.storeId);
    onClose();
  };

  const resetFilters = () => {
    setFilters({
      ...emptyFilters,
      storeId: selectedStoreId || "",
    });
    setResults([]);
    setSelectedVariantIds([]);
    setError("");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-7xl max-h-[92vh] overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 sm:px-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-primary-50 text-primary-700 flex items-center justify-center">
                <FiFilter size={21} />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Find Products
                </h2>

                <p className="mt-1 text-sm text-gray-500">
                  Filter variants, check availability, then add selected items.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
            aria-label="Close product filter"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-6 sm:p-8">
          {error && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <FilterSelect
              label="Store"
              value={filters.storeId}
              onChange={(value) => updateFilter("storeId", value)}
              placeholder="All accessible stores"
              options={filterOptions?.stores || []}
            />

            <FilterSelect
              label="Brand"
              value={filters.brandId}
              onChange={(value) => updateFilter("brandId", value)}
              placeholder="All brands"
              options={filterOptions?.brands || []}
            />

            <FilterSelect
              label="Category"
              value={filters.categoryId}
              onChange={(value) => updateFilter("categoryId", value)}
              placeholder="All categories"
              options={filterOptions?.categories || []}
            />

            <FilterSelect
              label="Subcategory"
              value={filters.subCategoryId}
              onChange={(value) => updateFilter("subCategoryId", value)}
              placeholder="All subcategories"
              options={subCategories}
            />

            <FilterSelect
              label="Season"
              value={filters.seasonId}
              onChange={(value) => updateFilter("seasonId", value)}
              placeholder="All seasons"
              options={filterOptions?.seasons || []}
            />

            <FilterSelect
              label="Colour"
              value={filters.colorId}
              onChange={(value) => updateFilter("colorId", value)}
              placeholder="All colours"
              options={filterOptions?.colors || []}
            />

            <FilterSelect
              label="Size"
              value={filters.sizeId}
              onChange={(value) => updateFilter("sizeId", value)}
              placeholder="All sizes"
              options={filterOptions?.sizes || []}
            />

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                GST rule group
              </label>
              <select value={filters.gstRuleGroup} onChange={(event) => updateFilter("gstRuleGroup", event.target.value)} className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700">
                <option value="">All GST rules</option>
                {(filterOptions?.gstRuleGroups || []).map((ruleGroup) => <option key={ruleGroup} value={ruleGroup}>{String(ruleGroup).replace(/_/g, " ")}</option>)}
              </select>
            </div>

            <FilterSelect
              label="Product"
              value={filters.productId}
              onChange={(value) => updateFilter("productId", value)}
              placeholder="All products"
              options={products.map((product) => ({
                ...product,
                name: `${product.name}${
                  product.productCode ? ` (${product.productCode})` : ""
                }`,
              }))}
            />

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Product, SKU, CHARMS barcode, supplier/EAN barcode, or variant
              </label>

              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  value={filters.query}
                  onChange={(event) =>
                    updateFilter("query", event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      searchProducts();
                    }
                  }}
                  placeholder="Search product, SKU, CHARMS barcode, or supplier/EAN barcode..."
                  className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Lot, supplier batch, GRN, or invoice
              </label>
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={filters.lotSearch} onChange={(event) => updateFilter("lotSearch", event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") searchProducts(); }} placeholder="e.g. LOT-0000001 or supplier batch number" className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700" />
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              onClick={resetFilters}
              className="h-11 rounded-xl border border-gray-200 px-5 text-sm font-medium hover:bg-gray-50"
            >
              Reset
            </button>

            <button
              onClick={searchProducts}
              disabled={isSearching}
              className="h-11 rounded-xl bg-primary-700 px-5 text-sm font-medium text-white flex items-center gap-2 disabled:opacity-60"
            >
              {isSearching ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiSearch />
              )}
              {isSearching ? "Searching..." : "Apply Filters"}
            </button>
          </div>

          <div className="mt-7 overflow-hidden rounded-2xl border border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-4">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Filtered Variants
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  {results.length} result{results.length === 1 ? "" : "s"} ·{" "}
                  {selectedVariantIds.length} selected
                </p>
              </div>

              {results.length > 0 && (
                <button
                  onClick={() =>
                    setSelectedVariantIds(
                      selectedVariantIds.length === results.length
                        ? []
                        : results.map((variant) => variant.id),
                    )
                  }
                  className="rounded-xl border border-primary-200 bg-white px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                >
                  {selectedVariantIds.length === results.length
                    ? "Clear selection"
                    : "Select all results"}
                </button>
              )}
            </div>

            {results.length ? (
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full min-w-[1320px] text-sm"><thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500"><tr><th className="px-5 py-3">Select</th><th className="px-5 py-3">Product / variant</th><th className="px-5 py-3">Brand / category</th><th className="px-5 py-3">CHARMS barcode</th><th className="px-5 py-3">Supplier / EAN</th><th className="px-5 py-3">Lots / batch</th><th className="px-5 py-3 text-right">Price</th><th className="px-5 py-3 text-right">Available</th></tr></thead><tbody>
                {results.map((variant) => {
                  const checked = selectedVariantIds.includes(variant.id);
                  const stock = Number(variant.availableQuantity || 0);
                  const lotCount = (variant.inventoryLots || []).length;

                  const lots = variant.inventoryLots || [];
                  return (
                    <tr
                      key={variant.id}
                      onClick={() => toggleVariant(variant.id)}
                      className={`cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-primary-50 transition ${
                        checked ? "bg-primary-50/60" : "bg-white"
                      }`}
                    >
                      <td className="px-5 py-4"><div
                          className={`h-6 w-6 shrink-0 rounded-md border flex items-center justify-center ${
                            checked
                              ? "border-primary-700 bg-primary-700 text-white"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          {checked && <FiCheck size={15} />}
                        </div></td>
                      <td className="px-5 py-4"><p className="font-semibold text-gray-900">{variant.product?.name}</p><p className="mt-1 text-xs text-gray-500">{variant.product?.productCode ? `${variant.product.productCode} · ` : ""}{variant.sku} · {variant.color?.name || "—"} / {variant.size?.name || "—"}</p></td>
                      <td className="px-5 py-4"><p>{variant.product?.brand?.name || "—"}</p><p className="mt-1 text-xs text-gray-500">{variant.product?.category?.name || "—"}</p></td>
                      <td className="px-5 py-4 font-mono text-xs">{variant.barcode || "—"}</td>
                      <td className="px-5 py-4 font-mono text-xs text-gray-600">{variant.supplierBarcode || "—"}</td>
                      <td className="px-5 py-4"><p className="text-xs text-gray-700">{lots.slice(0, 2).map((lot) => `${lot.lotNumber}${lot.supplierBatchNumber ? ` · ${lot.supplierBatchNumber}` : ""}`).join("\n") || "—"}</p>{lotCount > 2 && <p className="mt-1 text-xs text-gray-500">+{lotCount - 2} more lot(s)</p>}</td>
                      <td className="px-5 py-4 text-right font-semibold">{money(variant.sellingPrice || variant.mrp)}</td>
                      <td className={`px-5 py-4 text-right font-semibold ${stock > 0 ? "text-green-600" : "text-red-600"}`}>{stock}</td>
                    </tr>
                  );
                })}</tbody></table>
              </div>
            ) : (
              <div className="px-6 py-16 text-center text-gray-500">
                <FiPackage className="mx-auto mb-3 text-gray-300" size={32} />
                Apply filters to view matching product variants.
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
          <button
            onClick={onClose}
            className="h-11 rounded-xl border border-gray-200 px-6 font-medium hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={addSelectedProducts}
            disabled={!selectedVariantIds.length}
            className="h-11 rounded-xl bg-primary-700 px-6 font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <FiPlus />
            Add {selectedVariantIds.length} Selected Product
            {selectedVariantIds.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
      >
        <option value="">{placeholder}</option>

        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
            {option.code ? ` (${option.code})` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
